import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useState } from 'react';
import { Container, Box, Chip, Typography, Stack, Paper, Button, DialogContentText } from '@mui/material';
import { NavigateNext } from '@mui/icons-material';
import {
  Accordion,
  Breadcrumbs,
  Modal,
  NoContent,
  PositionedMenu,
  ErrorModal,
  InactiveAreaTaxModal,
} from '../../../components/shared';
import useStyles from './style';
import MachinesTable from './MachinesTable';
import LoadingSpinner from '../../../components/LoadingSpinner';
import {
  useGetSublocationByIdQuery,
  useGetSublocationMachinesByIdQuery,
  useUpdateSubLocationByIdMutation,
} from '../../../services/Sublocation/SublocationService';
import BREADCRUMB_NAMES from '../../../constants/breadcrumbNames';
import { routeConstants } from '../../../constants/routeConstants';
import SubLocationDetailAccordion from './SubLocationDetailAccordion';
import LocationOwnerDetailAccordion from '../../../components/LocationOwnerDetailAccordion';
import AssignNewMachine from './AssignNewMachine';
import { machineIcon, leaseIcon } from '../../../assets/images/icons/index';
import AssignNewLease from './AssignNewLease';
import LeaseDetailAccordion from './LeaseDetailAccordion';
import { useDispatch } from 'react-redux';
import { leaseDetailsFormAction } from '../../../redux-slice/lease';
import { useHasRoles, useSubLocationStatusValidation } from '../../../hooks';
import { ROLE } from '../../../constants/roles';
import RemitDetailsAccordion from './RemitDetailsAccordion';
import ConfirmationModal from '../../../components/ConfirmationModel/ConfirmationModal';
import { MESSAGE } from '../../../constants/message';
import SalesTaxHistoryModal from './SalesTaxHistoryModal';
import { ASSET_STATUS } from '../../../constants/assetStatus';

const SubLocationDetails = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [openAssignNew, setOpenAssignNew] = useState(false);
  const [openAssignNewLease, setOpenAssignNewLease] = useState(false);
  const [showAreaTaxHistoryModal, setShowAreaTaxHistoryModal] = useState(false);
  const [errorModal, setErrorModal] = useState({ open: false, message: '' });
  const [stateObj, setStateObj] = useState({});
  const isSuperAdminOrAccounting = useHasRoles([ROLE.SUPER_ADMIN_IT, ROLE.ACCOUNTING]);

  const dispatch = useDispatch();
  const canEdit = useHasRoles([
    ROLE.BUSDEV_ADMINISTRATION,
    ROLE.SUPER_ADMIN_IT,
    ROLE.CUST_SUPPORT,
    ROLE.SALES,
    ROLE.ACCOUNTING,
  ]);

  const {
    LOCATION: { LOCATION, LOCATION_DETAIL, SUB_LOCATION_DETAIL },
  } = BREADCRUMB_NAMES;
  const apiSearchParams = 'includeUsers=true&includeAssets=true&includeContracts=true';
  const apiSearchParamsForMachine = 'includeAssetsSublocation=true&includeAssetsModel=true';
  const {
    currentData: subLocationDetailsResponse,
    isLoading,
    isFetching,
    refetch: refetchSublocationDetails,
  } = useGetSublocationByIdQuery({ id: Number(id), apiSearchParams });

  const {
    currentData: subLocationMachineListResponse,
    isLoading: isSubLocationMachineListLoading,
    isFetching: isSubLocationMachineListFetching,
    isSuccess: isSubLocationMachineListSuccess,
    refetch: refetchSublocationMachineById,
  } = useGetSublocationMachinesByIdQuery({
    id: Number(id),
    apiSearchParams: apiSearchParamsForMachine,
  });
  const [updateSubLocationApi, { isLoading: updating }] = useUpdateSubLocationByIdMutation();

  const navigate = useNavigate();
  const { data: detailsResponse } = subLocationDetailsResponse ?? {};

  const classes = useStyles();
  const openErrorModal = (message) => setErrorModal({ open: true, message });
  const closeErrorModal = () => setErrorModal({ open: false, message: '' });

  // Calculate if there are active assets
  const assets = Array.isArray(detailsResponse?.assets) ? detailsResponse.assets : [];
  const isActiveAssets = assets.some((asset) => asset.statusId === ASSET_STATUS.ACTIVE);

  // Use custom hook for status validation
  const {
    onStatusToggleClick: onStatusToggleClickHandler,
    showInactiveAreaTaxModal,
    setShowInactiveAreaTaxModal,
    showWarningModal,
    setShowWarningModal,
    showArchiveModal,
    setShowArchiveModal,
  } = useSubLocationStatusValidation(detailsResponse, isActiveAssets);

  const breadcrumbData = [
    { text: LOCATION, redirection: `${routeConstants.LOCATION_ROUTE}` },
    {
      text: LOCATION_DETAIL,
      redirection: `${routeConstants.LOCATION_ROUTE}/${Number(searchParams.get('locationId'))}`,
    },
    { text: SUB_LOCATION_DETAIL },
  ];

  const editSubLocationClickHandler = () => {
    navigate(
      `/${routeConstants.SUBLOCATION_ROUTE}/${routeConstants.EDIT_SUB_LOCATION_ROUTE}/${Number(
        id,
      )}?locationId=${searchParams.get('locationId')}`,
    );
  };
  const editRemitClickHandler = () => {
    navigate(
      `/${routeConstants.SUBLOCATION_ROUTE}/${routeConstants.EDIT_REMIT_ROUTE}/${Number(
        id,
      )}?locationId=${searchParams.get('locationId')}`,
    );
  };

  const handleInactiveAreaTaxAction = () => {
    setShowInactiveAreaTaxModal(false);
    editSubLocationClickHandler();
  };

  const machineTableDisplay = () => {
    if (isSubLocationMachineListSuccess && subLocationMachineListResponse && subLocationMachineListResponse.data) {
      if (subLocationMachineListResponse?.data?.assets?.length <= 0 && !openAssignNew) {
        return (
          <Box className={classes.subLocDetailNoContent}>
            <NoContent
              icon={machineIcon}
              title="No Machines Associated"
              desc={
                <>
                  There is no machine associated to this sublocation.
                  <br />
                  Please add new machine
                </>
              }
              buttonName="Assign New"
              disabled={!canEdit}
              onBtnClickHandler={() => {
                setOpenAssignNew(true);
              }}
              className={classes.subLocationNoContentContainer}
            />
          </Box>
        );
      }

      return (
        <Box className={classes.subLocAssignedMachineWrap}>
          <Paper>
            {!openAssignNew ? (
              <Box className={classes.subLocAssignedMachineTable}>
                {isSubLocationMachineListLoading || isSubLocationMachineListFetching ? (
                  <LoadingSpinner containerHeight={400} />
                ) : (
                  <MachinesTable
                    machineList={subLocationMachineListResponse?.data?.assets}
                    openAssignNew={setOpenAssignNew}
                  />
                )}
              </Box>
            ) : (
              <AssignNewMachine
                sublocationId={detailsResponse?.id || Number(id)}
                openAssignNew={setOpenAssignNew}
                machineList={subLocationMachineListResponse?.data?.assets}
              />
            )}
          </Paper>
        </Box>
      );
    }
  };

  const onSaveAssignLeaseClickHandler = () => {
    setOpenAssignNewLease(false);
  };

  const onCancelAssignLeaseHandler = () => {
    setOpenAssignNewLease(false);
  };

  const dispatchLeaseValues = async () => {
    dispatch(
      leaseDetailsFormAction({
        leaseName: '',
        location: {
          id: detailsResponse?.location?.id,
          name: detailsResponse?.location?.locationName,
        },
        subLocation: [
          {
            id: detailsResponse?.id,
            name: detailsResponse?.name,
          },
        ],
        city: detailsResponse?.location?.city?.id || '',
        state: detailsResponse?.location?.state?.id || '',
        zipCode: detailsResponse?.location?.postcode || '',
        address1: detailsResponse?.location?.address1 || '',
        address2: detailsResponse?.location?.address2 || '',
        country: detailsResponse?.location?.country?.id || '',
        boothInfo: '',
        space: '',
        dba: '',
        leaseType: '',
        status: '',
        leaseBegins: null,
        leaseEnds: null,
        contractLink: detailsResponse?.location?.contractLink || 'N/A,',
        notes: detailsResponse?.location?.notes || '',
      }),
    );
  };

  const onAddNewLeaseClickHandler = () => {
    dispatchLeaseValues()
      .then(() => {
        navigate(
          `/${routeConstants.ADD_LEASE_ROUTE}?location=${Number(searchParams.get('locationId'))}&subLocation=${Number(
            id,
          )}`,
        );
      })
      .catch((err) => {
        console.log('error in dispatching action', err);
      });
  };

  const viewFullLeaseDetailsClickHandler = () => {
    const lease = detailsResponse?.contractSublocations[0];
    const leaseId = lease?.contract?.lease_id;
    navigate(`/${routeConstants.LEASE_ROUTE}/${leaseId}`);
  };

  const onArchiveClickHandler = () => {
    const status = detailsResponse?.status;
    updateSubLocationApi({
      id: id,
      status: !status,
    }).then((response) => {
      setShowArchiveModal(false);

      if (response.error) {
        const errorMessage = response.error?.data?.message || MESSAGE.SUBLOCATION_STATUS_UPDATE_FAILED;
        openErrorModal(errorMessage);
        return;
      }

      if (response.data) {
        refetchSublocationDetails();
        refetchSublocationMachineById();
      }
    });
  };

  const handleAreaTaxHistoryClick = () => {
    setStateObj({
      stateId: detailsResponse?.location?.state?.id,
      stateName: detailsResponse?.location?.state?.name,
    });
    setShowAreaTaxHistoryModal(true);
  };

  const handleStateTaxHistoryClick = () => {
    navigate(`/${routeConstants.SETTING_ROUTE}/${routeConstants.UPDATE_STATE_TAX_RATE}`, {
      state: {
        stateIdFromSublication: detailsResponse?.location?.state?.id,
        stateNameFromSublication: detailsResponse?.location?.state,
      },
    });
  };

  const leaseTableDisplay = () => {
    const lease = detailsResponse?.contractSublocations[0];
    if (detailsResponse?.contractSublocations?.length <= 0 && !openAssignNewLease) {
      return (
        <Box className={classes.subLocDetailNoContent} component={Paper}>
          <NoContent
            icon={leaseIcon}
            title="No Lease Found"
            desc={
              <>
                There is no Lease & Rent Agreement for this Sub Location.
                <br />
                Please add new or assign new Lease & Rent Agreement
              </>
            }
            buttonName="Assign New"
            secondBtnName="Add New"
            isTwoBtn={true}
            disabled={!canEdit}
            onBtnClickHandler={() => setOpenAssignNewLease(true)}
            onSecondBtnClickHandler={onAddNewLeaseClickHandler}
            className={classes.subLocationNoContentContainer}
          />
        </Box>
      );
    }
    return (
      <>
        {!openAssignNewLease ? (
          <Accordion
            marginBottom={3}
            title={'Lease & Rent Details'}
            content={
              isLoading || isFetching ? (
                <LoadingSpinner containerHeight={400} />
              ) : (
                <LeaseDetailAccordion leaseInfo={lease} />
              )
            }
            showMore
            subComponent={
              <PositionedMenu
                menuItemData={[
                  {
                    btnName: 'View Lease Details',
                    btnClickFunc: viewFullLeaseDetailsClickHandler,
                  },
                ]}
                disabled={!canEdit}
              />
            }
          />
        ) : (
          <AssignNewLease onSaveClick={onSaveAssignLeaseClickHandler} onCancel={onCancelAssignLeaseHandler} />
        )}
      </>
    );
  };

  return (
    <Container maxWidth="xl" disableGutters className={classes.root}>
      <Box>
        <Breadcrumbs
          icon={<NavigateNext fontSize="medium" />}
          variant="body2"
          underline="hover"
          data={breadcrumbData}
        />
      </Box>
      <Box mb={4}>
        <Stack direction={'row'} alignItems="center" spacing={1.5}>
          <Typography variant="h1" data-testid="locationName">
            {detailsResponse?.name}
          </Typography>
          <Chip
            label={detailsResponse?.status ? 'Active' : 'Inactive'}
            color={detailsResponse?.status ? 'success' : 'error'}
            variant="outlined"
          />
        </Stack>
      </Box>
      <Box mb={3}>
        <Accordion
          defaultExpanded
          marginBottom={3}
          title={'Sub Location Details'}
          content={
            isLoading || isFetching ? (
              <LoadingSpinner containerHeight={400} />
            ) : (
              <SubLocationDetailAccordion subLocationInfo={detailsResponse} />
            )
          }
          showMore
          subComponent={
            <PositionedMenu
              menuItemData={[
                {
                  btnName: 'Edit',
                  btnClickFunc: editSubLocationClickHandler,
                },
                ...(isSuperAdminOrAccounting
                  ? [
                      {
                        btnName: 'Area Tax History',
                        btnClickFunc: handleAreaTaxHistoryClick,
                      },
                      {
                        btnName: 'State Tax History',
                        btnClickFunc: handleStateTaxHistoryClick,
                      },
                    ]
                  : []),
                {
                  btnName: detailsResponse?.status ? 'Archive Sub-location' : 'Activate Sub-location',
                  btnClickFunc: onStatusToggleClickHandler,
                  btnTypeStyle: detailsResponse?.status ? 'dangerBtn' : 'successBtn',
                },
              ]}
              disabled={!canEdit}
            />
          }
        />
      </Box>
      <Box mb={3}>
        <Accordion
          marginBottom={3}
          title={'Remit Details'}
          content={
            isLoading || isFetching ? (
              <LoadingSpinner containerHeight={400} />
            ) : (
              <RemitDetailsAccordion subLocationInfo={detailsResponse} />
            )
          }
          showMore
          subComponent={
            <PositionedMenu
              menuItemData={[
                {
                  btnName: 'Edit',
                  btnClickFunc: editRemitClickHandler,
                },
              ]}
              disabled={!canEdit}
            />
          }
        />
      </Box>
      <Box mb={3}>{leaseTableDisplay()}</Box>
      <Box mb={3} className={classes.subLocationOwnerWrapper}>
        <Accordion
          title={'Sub Location Owner'}
          content={
            isLoading || isFetching ? (
              <LoadingSpinner />
            ) : (
              <LocationOwnerDetailAccordion ownerInfo={detailsResponse?.location?.owner} />
            )
          }
        />
      </Box>

      <Box component={Paper}>{machineTableDisplay()}</Box>
      <ConfirmationModal
        isLoading={updating}
        isOpen={showArchiveModal}
        title="Confirmation"
        msg={`Are you sure you want to ${
          detailsResponse?.status ? 'archive' : 'active'
        } this sub location? This process cannot be undone.`}
        buttons={[
          { text: detailsResponse?.status ? 'Archive' : 'Active', value: true },
          { text: 'Cancel', value: false },
        ]}
        onClick={(value) => {
          if (value) onArchiveClickHandler();
          else setShowArchiveModal(false);
        }}
      />
      <Modal
        title={MESSAGE.ARCHIVE_SUBLOCATION__WARNING_TITLE}
        titleVariant="subtitle10"
        open={showWarningModal}
        onClose={() => setShowWarningModal(false)}
        actionButtons={
          <Box my={3}>
            <Button
              size="large"
              onClick={() => {
                setShowWarningModal(false);
              }}
              variant="contained"
            >
              {MESSAGE.OK}
            </Button>
          </Box>
        }
      >
        <Box>
          <DialogContentText variant="body1">{MESSAGE.ARCHIVE_SUBLOCATION_WARNING_DESCRIPTION}</DialogContentText>
        </Box>
      </Modal>
      <InactiveAreaTaxModal
        open={showInactiveAreaTaxModal}
        onClose={() => setShowInactiveAreaTaxModal(false)}
        actionLabel="EDIT SUB LOCATION"
        onAction={handleInactiveAreaTaxAction}
      />
      <ErrorModal open={errorModal.open} onClose={closeErrorModal} message={errorModal.message} />
      {/* Area Tax History Modal */}
      <Modal
        open={showAreaTaxHistoryModal}
        onClose={() => setShowAreaTaxHistoryModal(false)}
        title={
          detailsResponse?.areaTaxLocations
            ? `Show the Area Tax History for ${detailsResponse?.areaTaxLocations?.areaTaxLocationName}`
            : ''
        }
        titleVariant="subtitle10"
        closeIcon={true}
        PaperProps={{
          sx: {
            width: '75%',
            maxWidth: 'none',
            maxHeight: '60%',
            height: '70%',
            overflow: 'hidden',
          },
        }}
      >
        <SalesTaxHistoryModal sublocation={detailsResponse} taxModal="Area" stateIdObj={stateObj} />
      </Modal>
    </Container>
  );
};

export default SubLocationDetails;
